import React, { useCallback, useEffect, useRef, useState } from 'react';
import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import OutsideClickHandler from 'react-outside-click-handler';
import './TestsResults.scss';
import Button from '../../BaseComponents/Button';
import { useDispatch, useSelector } from 'react-redux';
import {
  changeTestResultsModalVisibility,
  setMemberBiomarkersTestResultsList,
  setTestResultsModalMode,
  setShowBiomarkerTestResultModalVisibility,
  setSelectedBiomarkerResult,
  changeDeleteTestResultModalVisibility,
  setTestResultIdToDelete,
  setAppointmentsForBiomarkers,
  appendAppointmentsForBiomarkers,
  setBiomarkerAppointmentDateFilter,
  setBiomarkerResultsSearchTerm,
  setBiomarkerResultsBodySystem
} from '../../../store/memberSlice';
import TestResultsModal from './TestResultsModal';
import { RootState } from '../../../store/store';
import PublishedTestResultsTable from './PublishedTestResultsTable';
import { getBiomarkerAppointments, getMemberBiomarkersResults } from '../../../api/biomarkers';
import Loader from '../../BaseComponents/Loader';
import BiomarkerDetailModal from '../../BiomarkerDetailModal';
import Select from '../../BaseComponents/Select';
import {
  BIOMARKER_CATEGORY_FILTER_OPTIONS,
  filterBiomarkersByCategory
} from '../../../utils/biomarkerCategories';
import { MeasurementUnits } from '../../../types/apiGeneratedTypes';
import { sortBiomarkersByCategory } from '../../../utils/biomarkerCategories';

const APPOINTMENTS_PAGE_LIMIT = 10;

const formatAppointmentFilterDate = (dateString: string): string => {
  if (!dateString) return '';

  try {
    return formatInTimeZone(parseISO(dateString), 'Europe/London', 'dd/MM/yyyy');
  } catch {
    return dateString.split('T')[0] ?? dateString;
  }
};

const TestResults: React.FC = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const [openMenu, setOpenMenu] = useState<boolean>(false);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [appointmentsFetchError, setAppointmentsFetchError] = useState<string | null>(null);
  const [hasMoreAppointments, setHasMoreAppointments] = useState(true);
  const appointmentsOffsetRef = useRef(0);
  const menuScrollRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<number | null>(null);
  const assigned_doctor = JSON.parse(sessionStorage.getItem("assigned_doctor") ?? "false");
  const {admin: { role }} = useSelector((state: RootState) => state.admin);
  const visible = ((role !== "doctor") || (assigned_doctor && role === "doctor"));    
  const {
    isAddTestResultsModalOpened,
    memberData,
    isShowBiomarkerTestResultModalOpened,
    testResultsModalMode,
    selectedBiomarkerResult,
    memberBiomarkersTestResultsList,
    biomarkerAppointmentsList,
    biomarkerAppointmentDateFilter,
    biomarkerResultsSearchTerm,
    biomarkerResultsBodySystem
  } = useSelector((state: RootState) => state.member);

  const fetchMemberBiomarkersResults = useCallback(
    async (search?: string, appointmentDate?: string) => {
      if (!memberData?.user_id) return;

      try {
        setIsLoading(true);
        setError(null);
        const resp = await getMemberBiomarkersResults(
          memberData.user_id,
          search,
          undefined,
          undefined,
          appointmentDate
        );
        const sortedBiomarkers = sortBiomarkersByCategory(resp.biomarkers);
        dispatch(setMemberBiomarkersTestResultsList(sortedBiomarkers));
      } catch (fetchError) {
        setError(fetchError);
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, memberData?.user_id]
  );

  const fetchBiomarkerAppointments = useCallback(async () => {
    if (!memberData?.user_id) return;

    appointmentsOffsetRef.current = 0;
    setHasMoreAppointments(true);
    setAppointmentsLoading(true);
    setAppointmentsFetchError(null);

    try {
      const appointments = await getBiomarkerAppointments(
        APPOINTMENTS_PAGE_LIMIT,
        0,
        memberData.user_id
      );
      const dates = appointments.appointment_dates ?? [];
      dispatch(setAppointmentsForBiomarkers(dates));
      appointmentsOffsetRef.current = dates.length;
      setHasMoreAppointments(dates.length >= APPOINTMENTS_PAGE_LIMIT);
    } catch {
      setAppointmentsFetchError('Failed to load appointment dates');
      dispatch(setAppointmentsForBiomarkers([]));
    } finally {
      setAppointmentsLoading(false);
    }
  }, [dispatch, memberData?.user_id]);

  const loadMoreBiomarkerAppointments = useCallback(async () => {
    if (!memberData?.user_id || appointmentsLoading || !hasMoreAppointments) {
      return;
    }

    setAppointmentsLoading(true);

    try {
      const appointments = await getBiomarkerAppointments(
        APPOINTMENTS_PAGE_LIMIT,
        appointmentsOffsetRef.current,
        memberData.user_id
      );
      const newDates = appointments.appointment_dates ?? [];

      if (newDates.length > 0) {
        dispatch(appendAppointmentsForBiomarkers(newDates));
        appointmentsOffsetRef.current += newDates.length;
        setHasMoreAppointments(newDates.length >= APPOINTMENTS_PAGE_LIMIT);
      } else {
        setHasMoreAppointments(false);
      }
    } catch {
      setAppointmentsFetchError('Failed to load more appointment dates');
    } finally {
      setAppointmentsLoading(false);
    }
  }, [appointmentsLoading, dispatch, hasMoreAppointments, memberData?.user_id]);

  useEffect(() => {
    dispatch(setBiomarkerAppointmentDateFilter(''));
    dispatch(setBiomarkerResultsSearchTerm(''));
    dispatch(setBiomarkerResultsBodySystem(''));
    setOpenMenu(false);
    setAppointmentsFetchError(null);
    fetchBiomarkerAppointments();
    fetchMemberBiomarkersResults();
  }, [memberData?.user_id, dispatch, fetchBiomarkerAppointments, fetchMemberBiomarkersResults]);

  useEffect(() => {
    const scrollContainer = menuScrollRef.current;
    if (!scrollContainer || !openMenu) return;

    const handleScroll = () => {
      if (scrollTimeoutRef.current) {
        window.clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = window.setTimeout(() => {
        if (!scrollContainer || appointmentsLoading || !hasMoreAppointments) {
          return;
        }

        const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
        const threshold = 100;
        const isNearBottom =
          scrollTop + clientHeight >= scrollHeight - threshold;
        const isContentSmallerThanContainer =
          scrollHeight <= clientHeight + threshold;

        if (isNearBottom || isContentSmallerThanContainer) {
          loadMoreBiomarkerAppointments();
        }
      }, 150);
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });

    if (hasMoreAppointments && biomarkerAppointmentsList.length > 0) {
      setTimeout(() => handleScroll(), 100);
    }

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        window.clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [
    openMenu,
    loadMoreBiomarkerAppointments,
    appointmentsLoading,
    hasMoreAppointments,
    biomarkerAppointmentsList.length
  ]);

  const onSelectAppointmentFilter = (option: string) => {
    dispatch(setBiomarkerAppointmentDateFilter(option));
    setOpenMenu(false);
    fetchMemberBiomarkersResults(
      biomarkerResultsSearchTerm || undefined,
      option || undefined
    );
  };

  const appointmentFilterLabel = biomarkerAppointmentDateFilter
    ? formatAppointmentFilterDate(biomarkerAppointmentDateFilter)
    : 'Biomarkers by date/appointment';

  const handleSearchChange = (value: string) => {
    dispatch(setBiomarkerResultsSearchTerm(value));
    fetchMemberBiomarkersResults(
      value || undefined,
      biomarkerAppointmentDateFilter || undefined
    );
  };

  const handleCategoryFilterChange = (option: { key: string; value: string }) => {
    dispatch(setBiomarkerResultsBodySystem(option.key));
  };

  const selectedCategoryFilter = biomarkerResultsBodySystem
    ? BIOMARKER_CATEGORY_FILTER_OPTIONS.find(
        (option) => option.key === biomarkerResultsBodySystem
      ) ?? { key: '', value: '' }
    : { key: '', value: '' };

  const filteredBiomarkersCount = biomarkerResultsBodySystem
    ? filterBiomarkersByCategory(
        memberBiomarkersTestResultsList ?? [],
        biomarkerResultsBodySystem
      ).length
    : memberBiomarkersTestResultsList?.length ?? 0;

  const handleAddTestResultsClick = () => {
    dispatch(changeTestResultsModalVisibility(true));
    dispatch(setTestResultsModalMode('create'));
  };

  return (
    <div className='tests-container'>
      <div className='tests-header-row sideBarPad2'>
        <div className='search-and-filter-container d-flex align-items-center gap-20'>
          <div className='search-input-container'>
            <input
              type='text'
              className='search-input'
              placeholder='Search Biomarkers...'
              value={biomarkerResultsSearchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
        </div>
        <div className='tests-category-filter'>
          <Select
            title=''
            isError={false}
            name='biomarkerCategory'
            selectedOption={selectedCategoryFilter}
            onChange={handleCategoryFilterChange}
            placeholder='All categories'
            options={[
              { key: '', value: 'All categories' },
              ...BIOMARKER_CATEGORY_FILTER_OPTIONS
            ]}
          />
        </div>

        <div className='member-nav__dropdown tests-appointment-filter'>
          <OutsideClickHandler onOutsideClick={() => setOpenMenu(false)}>
            <button
              type='button'
              className={`tests-appointment-filter__trigger ${openMenu || biomarkerAppointmentDateFilter ? 'active' : ''}`}
              onClick={() => setOpenMenu(!openMenu)}
              aria-haspopup='menu'
              aria-expanded={openMenu}
            >
              {appointmentFilterLabel}
              <span className='member-nav__caret' aria-hidden='true' />
            </button>
            {openMenu && (
              <div
                ref={menuScrollRef}
                className='member-nav__menu tests-appointment-filter__menu'
                role='menu'
              >
                <button
                  role='menuitem'
                  type='button'
                  className={!biomarkerAppointmentDateFilter ? 'active' : ''}
                  onClick={() => onSelectAppointmentFilter('')}
                >
                  All
                </button>
                {appointmentsFetchError && (
                  <div className='tests-appointment-filter__error' role='alert'>
                    {appointmentsFetchError}
                  </div>
                )}
                {biomarkerAppointmentsList.map((date) => (
                  <button
                    key={date}
                    role='menuitem'
                    type='button'
                    className={
                      biomarkerAppointmentDateFilter === date ? 'active' : ''
                    }
                    onClick={() => onSelectAppointmentFilter(date)}
                  >
                    {formatAppointmentFilterDate(date)}
                  </button>
                ))}
                {appointmentsLoading && (
                  <div className='tests-appointment-filter__loading'>
                    Loading...
                  </div>
                )}
              </div>
            )}
          </OutsideClickHandler>
        </div>
        <div className='tests-header-actions d-flex align-items-center'>
          {visible && (
            <Button
              isSecondary={true}
              text='Add Biomarker Results'
              onClick={handleAddTestResultsClick}
            />
          )}
        </div>
      </div>
      <div className='count-container'>
        {filteredBiomarkersCount || 0}{' '}
        {filteredBiomarkersCount === 1 ? 'Biomarker' : 'Biomarkers'}
      </div>
      {isLoading ? (
        <Loader />
      ) : error ? (
        <div>Error loading biomarkers results</div>
      ) : (
        <>
          <PublishedTestResultsTable />
          {isAddTestResultsModalOpened && (
            <TestResultsModal
              isOpen={isAddTestResultsModalOpened}
              onClose={() => dispatch(changeTestResultsModalVisibility(false))}
              mode={testResultsModalMode}
              selectedAppointmentIdForTestResults={
                selectedBiomarkerResult?.result.appointment_id!
              }
              userId={memberData?.user_id!}
              testResultsData={[
                {
                  biomarker: {
                    ...selectedBiomarkerResult?.biomarker,
                    male_range_data:
                      selectedBiomarkerResult?.biomarker.male_range_data!,
                    id: selectedBiomarkerResult?.biomarker.id ?? null,
                    name: selectedBiomarkerResult?.biomarker.name ?? '',
                    body_systems:
                      selectedBiomarkerResult?.biomarker.body_systems ?? [],
                    description:
                      selectedBiomarkerResult?.biomarker.description ?? '',
                    measurement_unit:
                      (selectedBiomarkerResult?.biomarker
                        .measurement_unit as MeasurementUnits) ??
                      MeasurementUnits.CellsΜL,
                    foods_to_avoid:
                      selectedBiomarkerResult?.biomarker.foods_to_avoid ?? [],
                    foods_to_eat:
                      selectedBiomarkerResult?.biomarker.foods_to_eat ?? [],
                    biomarker_type:
                      selectedBiomarkerResult?.biomarker.biomarker_type ?? ''
                  },
                  result: selectedBiomarkerResult?.result!
                }
              ]}
            />
          )}
          {isShowBiomarkerTestResultModalOpened &&
            selectedBiomarkerResult &&
            memberData?.user_id && (
              <BiomarkerDetailModal
                isOpen={isShowBiomarkerTestResultModalOpened}
                onClose={() => {
                  dispatch(setShowBiomarkerTestResultModalVisibility(false));
                  dispatch(setSelectedBiomarkerResult(null));
                }}
                biomarkerResult={selectedBiomarkerResult}
                memberId={memberData.user_id}
                memberName={
                  memberData
                    ? `${memberData.first_name} ${memberData.last_name}`
                    : undefined
                }
                memberDob={memberData?.dob}
                memberGender={memberData?.gender}
                onEdit={() => {
                  dispatch(changeTestResultsModalVisibility(true));
                  dispatch(setTestResultsModalMode('update'));
                  dispatch(setShowBiomarkerTestResultModalVisibility(false));
                }}
                onDelete={() => {
                  dispatch(changeDeleteTestResultModalVisibility(true));
                  dispatch(
                    setTestResultIdToDelete(selectedBiomarkerResult?.result.id!)
                  );
                  dispatch(setShowBiomarkerTestResultModalVisibility(false));
                }}
                onResultDeleted={() => {
                  fetchMemberBiomarkersResults(
                    biomarkerResultsSearchTerm || undefined,
                    biomarkerAppointmentDateFilter || undefined
                  );
                }}
                showActions={visible}
              />
            )}
        </>
      )}
    </div>
  );
};
export default TestResults;
